import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api';
import './AssignGuardianModal.css';

const AssignGuardianModal = ({ 
  isOpen, 
  onClose, 
  propertyId, 
  roomId, 
  onAssignSuccess,
  propertyName = "",
  roomName = ""
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    property_id: propertyId || '',
    room_id: roomId || '',
    guardian_officer_id: '',
    selectedGuardians: []
  });
  
  const [guardians, setGuardians] = useState([]);
  const [officers, setOfficers] = useState({});
  const [selectedGuardianData, setSelectedGuardianData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOfficers, setIsLoadingOfficers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [errors, setErrors] = useState({});

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load guardian officers on modal open
  useEffect(() => {
    if (isOpen) {
      try {
        loadGuardianOfficers();
        setFormData(prev => ({
          ...prev,
          property_id: propertyId || '',
          room_id: roomId || ''
        }));
        setErrors({});
        setSelectedGuardianData(null);
        setSearchQuery('');
        setGuardians([]);
      } catch (error) {
        console.error('Error initializing modal:', error);
        toast.error('Failed to initialize modal. Please try again.');
      }
    }
  }, [isOpen, propertyId, roomId]);

  // Search guardians when query changes
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchQuery.length >= 1) {
        searchGuardians(searchQuery);
      } else {
        setGuardians([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchQuery]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadGuardianOfficers = async () => {
    try {
      setIsLoadingOfficers(true);
      const response = await api.get('/rooms/guardian-officers');
      const data = response.data;
      
      if (data.success) {
        setOfficers(data.officers || {});
      } else {
        console.error('Failed to load officers:', data.message);
        toast.error('Failed to load guardian officers');
      }
    } catch (error) {
      console.error('Error loading officers:', error);
      toast.error('Failed to load guardian officers');
    } finally {
      setIsLoadingOfficers(false);
    }
  };

  const searchGuardians = async (query) => {
    if (!query) return;
    
    try {
      setIsSearching(true);
      console.log('🔍 [DEBUG] Searching guardians with query:', query);
      
      // Laravel returns direct array, not wrapped in success/data
      // Format: [{ id, unique_guardian_number, user: { first_name, last_name, email } }]
      // Note: baseURL already includes /api, so don't add it again
      const response = await api.get(`/guardians/search/${encodeURIComponent(query)}`);
      const data = response.data;
      
      console.log('🔍 [DEBUG] Guardian search response:', data);
      
      // Laravel returns direct array
      if (Array.isArray(data)) {
        setGuardians(data);
        setShowDropdown(data.length > 0);
        console.log('🔍 [DEBUG] Found guardians:', data.length);
      } else if (data && data.success && Array.isArray(data.data)) {
        // Fallback for wrapped response
        setGuardians(data.data);
        setShowDropdown(data.data.length > 0);
        console.log('🔍 [DEBUG] Found guardians (wrapped):', data.data.length);
      } else {
        console.error('Invalid guardian search response:', data);
        setGuardians([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('Error searching guardians:', error);
      setGuardians([]);
      setShowDropdown(false);
      toast.error('Failed to search guardians. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleGuardianSelect = (guardian) => {
    setSelectedGuardianData(guardian);
    setFormData(prev => ({
      ...prev,
      selectedGuardians: [guardian.id]
    }));
    
    // Laravel format: guardian.user.first_name, guardian.user.last_name, guardian.unique_guardian_number
    const guardianName = guardian.user 
      ? `${guardian.user.first_name || ''} ${guardian.user.last_name || ''}`.trim()
      : guardian.name || 'Unknown';
    const guardianNumber = guardian.unique_guardian_number || guardian.ugd || 'N/A';
    
    // Set search query to show selected guardian (matching Laravel typeahead behavior)
    setSearchQuery(`${guardianName} (${guardianNumber})`);
    setShowDropdown(false);
    setErrors(prev => ({ ...prev, selectedGuardians: null }));
    
    console.log('🔍 [DEBUG] Guardian selected:', { guardian, guardianName, guardianNumber });
  };

  const handleOfficerChange = (e) => {
    setFormData(prev => ({
      ...prev,
      guardian_officer_id: e.target.value
    }));
    setErrors(prev => ({ ...prev, guardian_officer_id: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.guardian_officer_id) {
      newErrors.guardian_officer_id = ['Guardian officer is required'];
    }

    if (!formData.selectedGuardians.length) {
      newErrors.selectedGuardians = ['Please select a guardian'];
    }

    if (!formData.property_id) {
      newErrors.property_id = ['Property is required'];
    }

    if (!formData.room_id) {
      newErrors.room_id = ['Room is required'];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);

      const response = await api.post('/rooms/api/assign-guardian', formData);
      const data = response.data;

          if (data.success) {
            toast.success(data.message || 'Guardian assigned successfully!');
            
            // Close modal first
            handleClose();
            
            // Laravel behavior: Immediately redirect to payment creation page
            if (data.data?.redirectTo) {
              console.log('✅ [ASSIGN GUARDIAN] Redirecting to:', data.data.redirectTo);
              // Use setTimeout to ensure modal closes before navigation
              setTimeout(() => {
                navigate(data.data.redirectTo);
              }, 100);
            } else if (onAssignSuccess) {
              // Fallback: Call success handler if no redirect URL
              onAssignSuccess(data.data);
            }
            
            console.log('✅ [ASSIGN GUARDIAN] Guardian assigned successfully');
          } else {
        if (data.errors) {
          setErrors(data.errors);
        }
        toast.error(data.message || 'Failed to assign guardian');
      }
    } catch (error) {
      console.error('Error assigning guardian:', error);
      
      // Handle axios error responses
      if (error.response?.status === 422 && error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        toast.error(error.response.data.message || 'Validation failed');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('An error occurred while assigning guardian');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      property_id: '',
      room_id: '',
      guardian_officer_id: '',
      selectedGuardians: []
    });
    setSelectedGuardianData(null);
    setSearchQuery('');
    setGuardians([]);
    setShowDropdown(false);
    setErrors({});
    setIsLoading(false);
    onClose();
  };

  // Safety check - prevent rendering if critical props are missing
  if (!isOpen) {
    return null;
  }
  
  // Validate required props to prevent crashes
  if (!propertyId) {
    console.error('AssignGuardianModal: propertyId is required');
    return (
      <div className="modal fade in" style={{ display: 'block', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog" style={{ margin: '30px auto', width: '90%', maxWidth: '600px' }}>
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" className="close" onClick={handleClose}>&times;</button>
              <h4 className="modal-title">Error</h4>
            </div>
            <div className="modal-body">
              <p>Property ID is missing. Please close and try again.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={handleClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal fade in assign-guardian-modal" style={{ display: 'block', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog" style={{ margin: '30px auto', width: '90%', maxWidth: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header">
            <button 
              type="button" 
              className="close btn-modal-close" 
              onClick={handleClose}
              disabled={isLoading}
            >
              &times;
            </button>
            <h4 className="modal-title">Assign guardian</h4>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            <h4>Here you may add Guardian to specific room directly</h4>

            <form onSubmit={handleSubmit}>
              <fieldset className="form-horizontal">
                
                {/* Guardian Search */}
                <div className="form-group">
                  <label htmlFor="guardian-search">Search for Guardian</label>
                  <div className="guardian-search-container">
                    <input
                      ref={searchInputRef}
                      className="form-control guardian-search-input"
                      id="guardian-search"
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Type Guardian name or UID (or *)"
                      disabled={isLoading}
                    />
                    
                    {isSearching && (
                      <div className="search-loading">Searching...</div>
                    )}
                    
                    {showDropdown && guardians.length > 0 && (
                      <div 
                        ref={dropdownRef}
                        className="guardian-dropdown"
                      >
                        {guardians.map((guardian) => {
                          // Laravel format: guardian.user.first_name, guardian.user.last_name, guardian.unique_guardian_number
                          const guardianName = guardian.user 
                            ? `${guardian.user.first_name || ''} ${guardian.user.last_name || ''}`.trim()
                            : guardian.name || 'Unknown';
                          const guardianNumber = guardian.unique_guardian_number || guardian.ugd || 'N/A';
                          
                          return (
                            <div
                              key={guardian.id}
                              className="guardian-option"
                              onClick={() => handleGuardianSelect(guardian)}
                            >
                              <strong>
                                {guardianName} ({guardianNumber})
                              </strong>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {errors.selectedGuardians && (
                    <div className="error-message">
                      {errors.selectedGuardians.join(', ')}
                    </div>
                  )}
                </div>

                {/* Selected Guardian Display */}
                {selectedGuardianData && (
                  <div className="selected-guardian-info">
                    <h5>Selected Guardian:</h5>
                    <p>
                      <strong>
                        {selectedGuardianData.name || 
                         (selectedGuardianData.user ? `${selectedGuardianData.user.first_name || ''} ${selectedGuardianData.user.last_name || ''}`.trim() : 'Unknown')}
                      </strong>
                      <br />
                      Guardian ID: {selectedGuardianData.unique_guardian_number || selectedGuardianData.ugd || 'N/A'}
                      <br />
                      Email: {selectedGuardianData.email || (selectedGuardianData.user?.email || 'N/A')}
                      <br />
                      Mobile: {selectedGuardianData.mobile_number || (selectedGuardianData.user?.mobile_number || 'N/A')}
                    </p>
                  </div>
                )}

                {/* Guardian Officer Selection */}
                <div className="form-group">
                  <label htmlFor="guardian_officer_id">Guardian Officer</label>
                  <select
                    className="form-control"
                    id="guardian_officer_id"
                    name="guardian_officer_id"
                    value={formData.guardian_officer_id}
                    onChange={handleOfficerChange}
                    required
                    disabled={isLoading || isLoadingOfficers}
                  >
                    <option value="">Select guardian officer *</option>
                    {Object.entries(officers).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                  
                  {errors.guardian_officer_id && (
                    <div className="error-message">
                      {errors.guardian_officer_id.join(', ')}
                    </div>
                  )}
                </div>

                {/* Property Info Display */}
                {(propertyName || roomName) && (
                  <div className="property-info">
                    <h5>Assignment Details:</h5>
                    {propertyName && <p><strong>Property:</strong> {propertyName}</p>}
                    {roomName && <p><strong>Room:</strong> {roomName}</p>}
                  </div>
                )}

              </fieldset>

              {/* Submit Button */}
              <div className="form-group text-right">
                <button 
                  type="submit" 
                  className="btn btn-head btn-standard"
                  disabled={isLoading || isLoadingOfficers}
                >
                  {isLoading ? 'Assigning...' : 'Assign Guardian'}
                </button>
              </div>

            </form>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-default btn-modal-close"
              onClick={handleClose}
              disabled={isLoading}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignGuardianModal;
